import { create } from "zustand";

import {
	getAccount,
	signMessage,
	watchAccount,
	type GetAccountReturnType,
	type WatchAccountReturnType,
} from "wagmi/actions";

import {
	isAddress,
	type Address,
} from "@bracket/addressbook";

import { wagmiConfig, } from "@config";
import { getNewMessage, } from "./message";

import {
	assertClaimrLoaded,
	refereeFromQueryParams,
	performCheckin,
	resolveConfirmation,
	referUser,
	isUserRejectionError,
} from "./helpers";

import type * as Identity from "@bracket/identity/types";

import { initialize } from "react-ga";

/** [ wallet address, user_referred_flag, referral_submitted flag, user_referred_id, ] */
export declare type WalletAssoc = [Address, boolean, boolean, string | null, string | null, boolean];
export declare type WalletAssocr = {
	address: string;
	user_referral_id: string | null;
	user_referral_submitted: boolean;
	user_code: string | null;
	user_code_applied: boolean;
	user_code_valid: boolean;
};

export declare type PointsStore = {

	adjustments: Array<Identity.Adjustment>;

	/** FLAG for when the store is in the middle of transaction or busy in some way */
	busy: boolean;

	initialized: boolean;
	initializing: boolean;
	initializeAttempts: number;
	initializedForUser: boolean;
	initializingForUser: boolean;
	leaderboard: Array<{
		id: string;
		name: string;
		xp: number;
		cts: number;
		rank: number;
	}> | null;
	leaderboard_reduced: Array<{
		id: string;
		name: string;
		xp: number;
		cts: number;
		rank: number;
	}> | null;
	leaderboard_loading: boolean;
	leaderboard_rank: number;

	/** if they achieve 4 weeks of staking if user has 4 weeks */
	phase_boost: number;
	search_needle: string | null;
	ref_search_needle: string | null;
	season_one_points: number;
	season_one_ranking: number;

	user: Identity.User | null;

	user_code: string | null;
	user_code_valid: boolean;
	user_code_applied: boolean;

	/** currently performing the wallet/code process */
	user_code_applying: boolean;

	/** flag indicating that THIS user has been referred by another user */
	user_referred: boolean;

	/** the referral id this user utilized when signing up (the id THEY were provided) */
	user_referral_id: string | null;

	/** flag indicating that this user has successful transmitted their wallet and claimr has registered this user */
	user_referral_submitted: boolean;

	/** the referral id THIS USER can provide other users (use safary_referral_is) */
	users_referral_id: string | null;

	user_signing: boolean;

	/** the referral id THIS USER can provide other users (safary) */
	safary_referral_id: string | null;

	referrals: { address: Address; total_points: number }[];
	referrals_reduced: { address: Address; total_points: number }[] | null;

	referral_points_earned: number;

	ref_boost: number;

	early_deposit_boost: number;

	size_boost: number;

	staked: boolean;

	user_points: number;

	total_points: number;

	tvl_boost: number;

	data_available: boolean;

	wallet_assocs: Record<string, WalletAssocr>;

	wallet_codes: {
		address: Address;
		code: string;
	}[];

	initialize: () => Promise<void>;
	initializeForUser: () => Promise<void>;
	applySearchString: (pattern: string) => Promise<void>;
	onSearchUpdate: (value: string) => void | Promise<void>;
	onReferralSearchUpdate: (value: string) => void | Promise<void>;
	clearReferralSearch: () => void;
	clearSearch: () => void;
	sign: () => Promise<[boolean, string?]>;
	/** determine if the user should be signing, [ true/false, any message to go ] */
	shouldSign: () => [boolean, string | null];

	validateCode: (code: string, address: Address) => Promise<void>;
	bindWalletToCode: (wallet: Address) => Promise<void>;

	/** this inspects the url for variable "refer" in the query string, sets appropriate state if one is found */
	applyReferralState: () => Promise<void>;

	applyWalletAssoc: () => Promise<void>;
	attachAssocWallet: (assoc: WalletAssocr) => void;
	applyWalletAssocs: () => void;
	associateNewWallet: () => Promise<void>;
	associateAlternativeWallet: (address: Address) => void;
	associateExistingWallet: () => Promise<void>;
	checkin: () => Promise<void>;
	consumeQueryString: () => void;
	setUserCodeValid: (code: string) => void;
	reset: () => void;

	_onAccountChangeLsn: WatchAccountReturnType | null;

	_findAvatar: (address: Address) => Promise<unknown>;
	_loadAdjustments: () => Promise<void>;
	_loadLeaderBoard: () => Promise<void>;
	_loadUserData: () => Promise<void>;
	_loadReferralId: (force?: boolean) => Promise<void>;
	_loadReferrals: () => Promise<void>;
	_loadPoints: () => Promise<void>;
	_loadWalletPoints: () => Promise<void>;
	_bindListeners: () => void;
	_onPoll: NodeJS.Timeout | null;
	_onAccountChange: (account: GetAccountReturnType, prev: GetAccountReturnType) => void;
	_onUnhandled: () => void;
};


export const usePointsStore = create<PointsStore>()(
	// persist(
		(set, get) => ({
			adjustments: [],
			busy: false,
			early_deposit_boost: 0,
			initialized: false,
			initializedForUser: false,
			initializingForUser: false,
			initializeAttempts: 0,
			initializing: false,
			user_signing: false,
			leaderboard: null,
			leaderboard_reduced: null,
			leaderboard_loading: false,
			search_needle: null,
			ref_search_needle: null,
			season_one_points: 123456,
			season_one_ranking: 789,
			size_boost: 0,
			staked: false,
			tvl_boost: 0,
			user_points: 0,
			total_points: 0,
			ref_boost: 0,
			phase_boost: 0,
			leaderboard_rank: -1,

			user_code: null,
			user_code_valid: false,
			user_code_applied: false,
			user_code_applying: false,
			user_referred: false,
			user_referral_id: null,
			user_referral_submitted: false,
			users_referral_id: null,
			safary_referral_id: null,
			user: null,

			referrals: [],
			referrals_reduced: null,
			referral_points_earned: 0,

			_onAccountChangeLsn: null,
			_onPoll: null,

			data_available: false,

			wallet_assocs: {},
			wallet_codes: [],

			initialize: async () => {

				const {
					initialized,
					initializing,
				} = get();

				const {
					address,
					status,
				} = getAccount(wagmiConfig);

				assertClaimrLoaded();

				get()._bindListeners();

				if (status === "connected") {
					if (address && !initialized && !initializing) {

						set({ initializing: true, });

						await get().checkin();
						await get()._loadUserData();

						set({
							initialized: true,
							initializing: false,
						});

					}
				}

				if (status === "reconnecting") {

					const {
						initializeAttempts,
					} = get();
					
					set({ initializeAttempts: initializeAttempts + 1, });
					if (initializeAttempts >= 5) {
						set({ initializeAttempts: 0 });
						window.location.reload();
					}
				}
			},

			checkin: async () => {
				try {
					const { address } = getAccount(wagmiConfig);

					if (address) {
						const referee = refereeFromQueryParams();
						let user = await performCheckin(address, referee);
						if ((referee || user.referee) && !user.confirmed) {
							// temporary to migrate users off of their cache.
							user = await resolveConfirmation(user, get());
						}

						set({
							user,
							user_referred: Boolean(user.referee),
							user_referral_id: user.referee,
							user_referral_submitted: user.confirmed,
							users_referral_id: user.claimr_id,
							safary_referral_id: user.safary_id,
						});

						return;
					}
					throw new Error("attempted to load points store without a valid wagmi address set.");
				} catch (err) {
					console.warn("PointsStore::checkin has reached an unhandled exception.");
				}
			},

			sign: async () => {

				const { user, } = get();
				const { address, } = getAccount(wagmiConfig);
				const active_referee = refereeFromQueryParams();

				if(user && address) {
					if(active_referee) {

						try {
							const message = getNewMessage(active_referee);
							const signed = await signMessage(wagmiConfig, { message, });
							const [ success, payload ] = await referUser(user, active_referee);
							// biome-ignore lint/suspicious/noExplicitAny: will require ambient mode global definitions otherwise.
							(globalThis as any).claimr.connect_wallet(address.toLowerCase(), signed, message);
							if(success) {
								set({ user: payload, });
								return [ true, ];
							}
							return [false, payload]
						}
						catch( err: unknown ) {
							//ignore all errors - different wallets throw random horseshit errors whatever pleases them
							// if the user has rejected signage, fine - they will be asked again.
							return;
							// if(!isUserRejectionError(err)) {
							// 	throw err;
							// }
						}
					}
					console.warn("PointsStore::sign invoked without an active referee, ignoring.");
					return [true];
				}
				throw new Error("no user provided for the signing phase of this transaction.");
			},

			shouldSign: () => {
				const { user, } = get();
				if(user) {

					const active_referee = refereeFromQueryParams();

					const {
						confirmed,
						referee_id,
					} = user;

					//no ref link, move on.
					if(!active_referee) {
						return [false, null];
					}

					//never confirmed before, lets sign them to the active ref.
					if(!confirmed && active_referee) {
						return [true, null];
					}

					//user has signed but the reflink and user referee do not match.
					if(confirmed && active_referee !== referee_id) {
						return [ false, "You have already signed a referral link"];
					}
					// we have an reflink that they've signed with before.
					return [false, null];
				}
				throw new Error("PointsStore::attempted to sign without an active user");
			},

			initializeForUser: async () => {
				throw new Error("initialize for user was invoked");
				set({ initializingForUser: true });
				await get()._loadUserData();
				set({ initializedForUser: true, initializingForUser: false });
			},

			applyWalletAssoc: async () => {
				get().consumeQueryString();
				const { address } = getAccount(wagmiConfig);
				const { wallet_assocs, user_referral_id, user_referral_submitted } = get();

				let assoc = wallet_assocs[address as Address];
				if (!assoc) {
					await get().associateNewWallet();
				} else if (window.location.search.includes("ref=") || (window.location.search.includes("refer=") && (!user_referral_id || !user_referral_submitted))) {
					await get().associateExistingWallet();
				}
				assoc = wallet_assocs[address as Address];

				if (assoc) {
					set({
						user_referred: !!assoc.user_referral_id,
						user_referral_id: assoc.user_referral_id || null,
						user_referral_submitted: assoc.user_referral_submitted,
						user_code: assoc.user_code,
						user_code_applying: false,
						user_code_applied: assoc.user_code_applied,
						user_code_valid: assoc.user_code_valid,
					});
					return;
				}
				return;
				throw new Error("PointsStore::applyWalletAssoc failed to gain or create an associate wallet.");
			},

			attachAssocWallet: (assoc) => {
				set(assoc);
			},

			reset: () => {
				set({
					initialized: false,
					initializing: false,
					user_code: null,
					user_code_valid: false,
					user_code_applied: false,
					user_code_applying: false,
					user_referred: false,
					user_referral_id: null,
					user_referral_submitted: false,
					users_referral_id: null,
					safary_referral_id: null,
				});
			},

			associateNewWallet: async () => {
				throw new Error("pointstore called associateNewWallet");
				const { address } = getAccount(wagmiConfig);

				if (!address) throw new Error("PointsStore::associateNewWallet - expected an address from wagmi but got none");

				const { wallet_assocs, user_referral_id, user_signing, user_referral_submitted } = get();

				const assoc: WalletAssocr = {
					address,
					user_referral_id: user_referral_id || null,
					user_referral_submitted: false,
					user_code: null,
					user_code_applied: false,
					user_code_valid: false,
				};

				if (user_referral_id && !user_signing && !user_referral_submitted) {
					set({ user_signing: true });

					try {
						await new Promise((r) => setTimeout(() => r(true), 3000));
						const message = getNewMessage(user_referral_id);
						const signed = await signMessage(wagmiConfig, {
							message,
							account: address.toLowerCase() as Address,
						});

						set({ user_signing: false });
						(window as any).claimr.connect_wallet(address.toLowerCase(), signed, message);
						assoc.user_referral_submitted = true;
					} catch (err) {
						set({ user_signing: false });
						return;
					}
				} else {
					// console.log(">>>>>>>>>REFID", user_referral_id);
					// console.log(">>>>>>>>>SIGNING?", user_signing);
					// console.log("PointsStore::associateNewWallet - no ref id");
				}
				wallet_assocs[address] = assoc;
				set({ wallet_assocs });
				get().attachAssocWallet(assoc);
				return;
			},

			associateExistingWallet: async () => {
				throw new Error("pointstore called associateNewWallet");

				// console.log("ASSOC EXISTING");
				const { address, status } = getAccount(wagmiConfig);

				const { user_signing, user_referral_submitted } = get();

				if (address && status === "connected") {
					const { search } = window.location;
					const partial = search.slice(1, search.length);
					const parts = partial.split("&");
					const user_referral_id = parts.reduce((acc: any, part: any) => {
						if (acc) return acc;
						const [k, v] = part.split("=");
						if (k === "ref") return v;
						if (k === "refer") return v;
					}, null);

					if (user_referral_id && !user_signing && !user_referral_submitted) {
						const message = getNewMessage(user_referral_id);

						try {
							set({ user_signing: true });

							await new Promise((r) => setTimeout(() => r(true), 3000));
							const signed = await signMessage(wagmiConfig, {
								message,
								account: address.toLowerCase() as Address,
							});
							(window as any).claimr.connect_wallet(address.toLowerCase(), signed, message);
							const { wallet_assocs } = get();

							const mutation = {
								user_referral_id,
								user_referral_submitted: true,
							};

							Object.assign(wallet_assocs[address as Address], mutation), set(Object.assign({}, mutation, { user_signing: false }));
						} catch (err) {
							set({ user_signing: false });
							// do nothing, wagmi will error upon signatory cancellation.
							return;
						}
					}
				}
			},

			associateAlternativeWallet: (address: Address) => {
				throw new Error("pointstore called associateNewWallet");

				const { wallet_assocs, user_referral_id, user_code, user_code_applied, user_code_valid, user_referral_submitted } = get();

				if (!wallet_assocs[address]) {
					const assoc: WalletAssocr = {
						address,
						user_referral_id: null,
						user_code: null,
						user_code_applied: false,
						user_code_valid: false,
						user_referral_submitted: false,
					};

					wallet_assocs[address] = assoc;
					get().attachAssocWallet(assoc);
				} else {
					get().attachAssocWallet(wallet_assocs[address]);
				}
			},

			consumeQueryString: () => {
				const { search } = globalThis.location;
				if (search) {
					const vars = search
						.slice(1, search.length)
						.split("&")
						.map((str) => str.split("="));
					const referportion = vars.find(([key, value]) => key === "ref" || key === "refer");
					if (referportion) {
						const user_referral_id = referportion[1];
						if (user_referral_id) {
							set({
								user_referral_id,
								user_referred: true,
							});
							return;
						}
					}
				}
			},

			applyWalletAssocs: () => {
				throw new Error("PointsStore::applyWalletAssocs is deprecated!");
			},

			applyReferralState: async () => {
				throw new Error("PointsStore::applyReferalState is deprecated!");
			},

			applySearchString: async (pattern) => {
				throw new Error("PointsStore::applySearchString deprecated.");
			},

			clearSearch: () => {
				set({
					search_needle: null,
					leaderboard_reduced: null,
				});
			},

			onSearchUpdate: async (value: string) => {
				const { leaderboard } = get();
				if (leaderboard && value.length) {
					const leaderboard_reduced = leaderboard.filter(({ id }) => {
						if (id.toLowerCase().includes(value.toLowerCase())) return true;
					});
					set({ search_needle: value, leaderboard_reduced });
					return;
				}
				set({ search_needle: "", leaderboard_reduced: null });
			},

			onReferralSearchUpdate: async (value: string) => {
				const { referrals } = get();
				if (referrals && referrals.length && value.length) {
					const referrals_reduced = referrals.filter(({ address }) => {
						if (address.toLowerCase().includes(value.toLowerCase())) return true;
					}).sort((a, b) => a.tvl_points > b.tvl_points ? -1 : -1);
					set({ ref_search_needle: value, referrals_reduced });
					return;
				}
				set({ ref_search_needle: "", referrals_reduced: null });
			},

			clearReferralSearch: () => {
				set({ ref_search_needle: null, referrals_reduced: null });
			},

			validateCode: async (code, address: Address) => {
				throw new Error("pointstore called validateCode");

				const response = await fetch(`/claimr/code?code=${code}`);
				const { wallet_assocs } = get();
				if (response.ok) {
					const result = await response.json();
					if (result.data.valid) {
						const assoc = wallet_assocs[address];
						if (assoc) {
							assoc.user_code = code;
							assoc.user_code_applied = true;
							assoc.user_code_valid = true;
						}
						set({
							user_code: code,
							user_code_valid: true,
							// wallet_codes: [ ...get().wallet_codes, { address, code } ],
						});
					}
				}
			},

			setUserCodeValid: (userCode: string) => {
				throw new Error("pointstore called setUserCodeValid");
				set({ user_code: userCode, user_code_valid: true });
			},

			bindWalletToCode: async (wallet: Address) => {
				throw new Error("pointstore called bindWalletToCode");
				try {
					const { user_code, user_code_valid, user_code_applied, user_code_applying } = get();

					if (!user_code_applying && !user_code_applied && user_code_valid) {
						set({ user_code_applying: true });

						const response = await fetch(`/claimr/code`, {
							method: "POST",
							body: JSON.stringify({ code: user_code, used_by: wallet }),
						});

						if (response.ok) {
							const result = await response.json();
							if ("success" in result && result.success) {
								set({
									user_code_applied: true,
									user_code_applying: false,
								});
								return;
							}
							throw new Error("server response was not deemed successful");
						}
						throw new Error("errant server response");
					}
					// bail them out of this, we can recover but the state is not performant currently.
					return;
				} catch (err) {
					throw new Error(`failed binding wallet to invite code - ${err instanceof Error ? err.message : "no error instance"}`);
				}
			},

			registerWalletWithClaimr: async () => {},

			_loadLeaderBoard: async () => {
				set({ leaderboard_loading: true });

				const { address } = getAccount(wagmiConfig);
				const endpoint = address ? `/claimr/leaderboard?address=${address}` : "/claimr/leaderboard";
				const response = await fetch(endpoint);

				if (response.ok) {
					const result = await response.json();
					let {
						data: {
							leaderboard: { general: data, general_index: rank },
						},
					} = result;

					if (Array.isArray(data)) {
						const leaderboard = data.map((data, rank) => Object.assign({}, data, { rank: rank + 1 }));

						set({
							leaderboard,
							leaderboard_loading: false,
							leaderboard_rank: rank,
						});
						return;
					}
					throw new Error("cannot load leaderboard data even though it says OK");
				}
				throw new Error("cannot load leaderboard data");
			},

			_findAvatar: async (address) => {
				try {
					// const name = await getEnsName({ address, });
				} catch (err) {
					// console.log(">>>>>>>>>>>>>>NIOPE", err);
				}
			},

			_loadAdjustments: async () => {
				try {
					const { address, } = getAccount(wagmiConfig);
					if(address) {
						const response = await fetch(`/adjustments?wallet=${address}`);
						if(response.ok) {
							const result = await response.json();
							set({ adjustments: result.adjustments, });
							return;
						}
						throw new Error("invalid server response");
					}
				}
				catch(err) {
					console.warn("failed loading user adjustments");
				}
			},

			_loadUserData: async () => {
				try {
					const { address } = getAccount(wagmiConfig);
					if (address) {
						// load in the adjustments first so the other loaders have them ready.
						await get()._loadAdjustments();
						await Promise.all([
							get()._loadReferrals(),
							get()._loadWalletPoints(),
							get()._loadPoints(),
							get()._loadLeaderBoard(),
						]);
						// console.log("loaded user data");
						return;
					}
				} catch (err) {
					console.warn("failed loading user data");
				}
			},

			_loadReferralId: async (force = false) => {
				const { users_referral_id, safary_referral_id } = get();

				if (force || !safary_referral_id || !safary_referral_id.length) {
					const { address } = getAccount(wagmiConfig);
					if (address) {
						const response = await fetch(`/claimr/wallet/ref_id?address=${address}`);
						if (response.ok) {
							const result = await response.json();
							if (result.success) {
								set({
									users_referral_id: result.data.ref_id,
									safary_referral_id: result.data.saf_ref_id,
								});
								return;
							}
						}
					}
					set({
						users_referral_id: null,
						safary_referral_id: null,
					});
				}
			},

			_loadReferrals: async () => {
				try {
					const acct = getAccount(wagmiConfig);
					if (acct.address) {
						const response = await fetch(`/claimr/wallet/referrals?address=${acct.address}`);
						if (response.ok) {
							const result = await response.json();
							if (result.success) {
								const { data } = result;
								const { referrals } = data;
								const { adjustments, } = get();
								set({
									referrals: [
										...referrals,
										...adjustments.map(adj => adj.state, )
									].sort((a,b) =>
										a.tvl_points > b.tvl_points ? -1 : 1
									)
								})
								return;
							}
						}
						throw new Error("failed loading user referrals");
					}
				} catch (err) {
					console.warn("referral data will not be available until August 2nd");
				}
				set({ referrals: [], referral_points_earned: 0 });
			},

			_loadPoints: async () => {
				try {
					const response = await fetch("/claimr/points");
					if (response.ok) {
						const result = await response.json();
						const { total_points } = result.data;
						if (typeof total_points === "number") {
							set({ total_points, });
							return;
						}
					}
				}
				catch( err ) {
					console.warn("failed loading user points");
				}
			},

			_loadWalletPoints: async () => {
				const acct = getAccount(wagmiConfig);
				if (acct.address) {
					const response = await fetch(`/claimr/wallet/points?address=${acct.address}`);
					if (response.ok) {
						const result = await response.json();
						try {
							const { points, } = result.data;
							const { adjustments, } = get();

							const {
								early_deposit_boost,
								ref_boost,
								size_boost,
								total_points,
								xp,
								initial_points,
								tvl_boost,
								ref_points,
								phase_boost,
							} = points;

							const reassigned_points = adjustments.reduce((acc, adj) => {
								acc += adj.state.tvl_points * 0.2;
								return acc;
							}, 0);

							// // rollup adjustments to the figures below
							// const referral_points_earned = adjustments.reduce((acc, adj) => {
							// 	// acc += adj.state.tvl;
							// 	acc += adj.state.total_points;
							// 	return acc;
							// }, points.ref_points);

							set({
								early_deposit_boost,
								ref_boost,
								size_boost,
								user_points: (total_points || 0) + (xp || 0) + (initial_points || 0),
								tvl_boost,
								phase_boost,
								referral_points_earned: ref_points + reassigned_points,
							});

							return;
						} catch (err) {
							console.error(err);
							throw new Error("wallet point data is malformed");
						}
					}
				}
			},

			_bindListeners: () => {
				let {
					_onAccountChange,
					_onAccountChangeLsn,
					_onPoll,
				} = get();

				if (!_onAccountChangeLsn) {
					_onAccountChangeLsn = watchAccount(wagmiConfig, {
						onChange: _onAccountChange,
					});
					set({ _onAccountChangeLsn });
				}

				if (!_onPoll) {
					const gg = setInterval(() => get()._loadUserData(), 10000);
					console.log(gg);
					_onPoll = setInterval(() => get()._loadUserData(), 10000);
					set({ _onPoll });
				}
			},

			_onAccountChange: ({ address, status }: GetAccountReturnType, prev: GetAccountReturnType) => {
				if (status === "connected" && address) {
					const address_changed = address !== prev.address || !get().initialized;
					if (address_changed) {
						set({ initialized: false, initializing: false });
						get().initialize();
					}
				}
			},

			_onUnhandled: () => {
				set({ data_available: true });
			},
		}),
		// {
		// 	name: "bracket-points",
		// 	onRehydrateStorage: () => {
		// 		return (state, error) => {
		// 			if (error) {
		// 				console.log("an error happened during hydration", error);
		// 			} else {
		// 				if (state) {
		// 					if (state._onAccountChangeLsn) state._onAccountChangeLsn();
		// 					if (state._onPoll) clearInterval(state._onPoll);

		// 					state.initialized = false;
		// 					state.initializing = false;
		// 					state.user_signing = false;
		// 				}
		// 			}
		// 		};
		// 	},
		// },
	// ),
);
